import React, { useMemo, useState } from "react";
import { Box, Chip, Paper, useTheme } from "@mui/material";
import { DataGridPro, GridToolbar, useGridApiRef } from "@mui/x-data-grid-pro";
import { svSE, enUS } from "@mui/x-data-grid/locales";
import { useIntl } from "react-intl";
import { tokens } from "../../../assets/theme";
import { getAssetNodeTypeLabel } from "../cmmsMessages";
import {
  applyColumnWidths,
  readColumnVisibility,
  readStoredJson,
  reorderColumns,
  toStoredColumnVisibility,
} from "../cmmsGridPrefs";
import {
  getAssetHierarchyPalette,
  getDataGridPanelSx,
  getStatusChipSx,
  getTypeChipSx,
} from "../assetHierarchyStyles";
import { CmmsListSkeleton } from "./CmmsStates";
import messages from "../messages";

/*
 * The asset hierarchy as a flat table, the alternative to the tree tab.
 *
 * The tree is the right shape for walking the structure and the wrong shape for every other
 * question asked of it: which objects have no machine linked, show me every department, give me
 * this as a spreadsheet. Those are grid questions, and every other CMMS register already answers
 * them because it is a grid.
 *
 * Rows are flat, with a Path column carrying the ancestry, rather than DataGridPro's treeData.
 * The rows arrive from flattenAssetNodes in depth-first order — the tree's own reading order,
 * sortOrder included — and no sortModel is set here, so an untouched grid reads exactly like the
 * tree. Sorting a column then reorders the *whole* hierarchy, which treeData would have confined
 * to each parent's own children and which is the entire reason this tab exists.
 *
 * The rows are the page's own treeQuery data, so this issues no request: the two tabs cannot
 * disagree, and switching between them is instant.
 */
const columnOrderKey = "cmmsAssetHierarchy-columnOrder";
const columnWidthsKey = "cmmsAssetHierarchy-columnWidths";
const columnVisibilityKey = "cmmsAssetHierarchy-columnVisibility";

// The fourth CMMS grid to remember these, over the shared helpers so a malformed
// stored value costs a preference rather than the page.
const initFromStorage = (cols) => {
  let out = cols;
  const order = readStoredJson(columnOrderKey, null);
  if (order) out = reorderColumns(order, out);
  const widths = readStoredJson(columnWidthsKey, null);
  if (widths) out = applyColumnWidths(widths, out);
  return out;
};

/*
 * Id is hidden because nobody reads a hierarchy by primary key. Description and sort order are
 * hidden because they are long and rarely asked for — but both are one click away in Kolumner,
 * and both export. The nameplate columns are hidden because they are blank on every site and
 * department; they are there for the "which presses are Engel" question and for the export.
 */
const defaultColumnVisibility = {
  id: false,
  description: false,
  sortOrder: false,
  manufacturer: false,
  model: false,
  modelYear: false,
  serialNumber: false,
};

export default function AssetHierarchyTable({ nodes, isLoading, onSelectNode, emptyState }) {
  const theme = useTheme();
  const intl = useIntl();
  const apiRef = useGridApiRef();
  const language = localStorage.getItem("language") || "en";
  const translations = language === "sv" ? svSE : enUS;

  // Both of these build a fresh object on every call, so naming them in the columns dependency
  // array without this would memoise nothing and hand the grid a new column array every render.
  const mode = theme.palette.mode;
  const colors = useMemo(() => tokens(mode), [mode]);
  const cmmsColors = useMemo(() => getAssetHierarchyPalette(mode), [mode]);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() =>
    readColumnVisibility(columnVisibilityKey, defaultColumnVisibility));

  const baseColumns = useMemo(
    () => [
      { field: "id", headerName: intl.formatMessage(messages.columnId), width: 80 },
      { field: "name", headerName: intl.formatMessage(messages.fieldName), flex: 2, minWidth: 180 },
      {
        field: "path",
        headerName: intl.formatMessage(messages.columnPath),
        flex: 2,
        minWidth: 220,
        // Not a field on the row: flattenAssetNodes hands over the ancestry as an array, and the
        // separator is a presentation choice rather than something to store.
        valueGetter: (params) => (params.row.ancestorNames ?? []).join(" / "),
      },
      {
        field: "nodeType",
        headerName: intl.formatMessage(messages.fieldType),
        width: 150,
        // The cell is a chip, so without a value of its own the CSV would carry the raw enum
        // integer — the same trap the recurring schedules grid fell into with triggerBasis.
        valueGetter: (params) => getAssetNodeTypeLabel(params.row.nodeType, intl),
        renderCell: (params) => (
          <Chip label={params.value} size="small" variant="outlined" sx={getTypeChipSx(cmmsColors)} />
        ),
      },
      {
        field: "parentName",
        headerName: intl.formatMessage(messages.fieldParent),
        flex: 1,
        minWidth: 140,
      },
      {
        field: "linkedMachineName",
        headerName: intl.formatMessage(messages.machineLinkField),
        flex: 1,
        minWidth: 140,
        // Blank on anything that is not a linked maintenance object, which is most rows.
        valueGetter: (params) => params.row.linkedMachineName ?? "",
      },
      {
        field: "manufacturer",
        headerName: intl.formatMessage(messages.fieldManufacturer),
        flex: 1,
        minWidth: 140,
        valueGetter: (params) => params.row.manufacturer ?? "",
      },
      {
        field: "model",
        headerName: intl.formatMessage(messages.fieldModel),
        flex: 1,
        minWidth: 140,
        valueGetter: (params) => params.row.model ?? "",
      },
      {
        field: "modelYear",
        headerName: intl.formatMessage(messages.fieldModelYear),
        width: 120,
        type: "number",
        // A year, not a quantity: the number column would print 2019 as "2,019" in English.
        valueFormatter: (params) => (params.value == null ? "" : String(params.value)),
      },
      {
        field: "serialNumber",
        headerName: intl.formatMessage(messages.fieldSerialNumber),
        flex: 1,
        minWidth: 140,
        valueGetter: (params) => params.row.serialNumber ?? "",
      },
      {
        field: "description",
        headerName: intl.formatMessage(messages.fieldDescription),
        flex: 2,
        minWidth: 200,
        valueGetter: (params) => params.row.description ?? "",
      },
      {
        field: "sortOrder",
        headerName: intl.formatMessage(messages.fieldSortOrder),
        width: 120,
        type: "number",
      },
      {
        field: "isActive",
        headerName: intl.formatMessage(messages.columnStatus),
        width: 110,
        // Same reason as nodeType: the word, not `true`, is what belongs in the export.
        valueGetter: (params) =>
          intl.formatMessage(params.row.isActive ? messages.statusActive : messages.statusInactive),
        renderCell: (params) => (
          <Chip label={params.value} size="small" sx={getStatusChipSx(cmmsColors, params.row.isActive)} />
        ),
      },
    ],
    [cmmsColors, intl],
  );

  const columns = useMemo(() => initFromStorage(baseColumns), [baseColumns]);

  const gridSlots = useMemo(
    () => ({
      toolbar: GridToolbar,
      loadingOverlay: () => (
        <Box sx={{ px: 2, pt: 1.5, width: "100%" }}>
          <CmmsListSkeleton rows={10} height={36} />
        </Box>
      ),
      // Only when the caller supplied one — an overlay rendering `undefined` would cover the grid
      // with nothing.
      ...(emptyState ? {
        noRowsOverlay: () => (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
            {emptyState}
          </Box>
        ),
      } : {}),
    }),
    [emptyState],
  );

  const handleColumnOrderChange = () => {
    if (!apiRef.current) return;
    // Every column, not just the visible ones: a hidden column left out here is a hidden column
    // missing from the saved order, and it comes back at the far right once it is unhidden.
    const fields = apiRef.current.getAllColumns().map((c) => c.field);
    localStorage.setItem(columnOrderKey, JSON.stringify(fields));
  };

  const handleColumnResize = (params) => {
    const map = readStoredJson(columnWidthsKey, {});
    map[params.colDef.field] = params.width;
    localStorage.setItem(columnWidthsKey, JSON.stringify(map));
  };

  const handleColumnVisibilityModelChange = (model) => {
    setColumnVisibilityModel(model);
    const fields = baseColumns.map((col) => col.field);
    localStorage.setItem(columnVisibilityKey, JSON.stringify(toStoredColumnVisibility(model, fields)));
  };

  return (
    <Paper sx={{ position: "relative", borderRadius: "4px" }}>
      <Box
        height="70vh"
        sx={{
          "& .MuiDataGrid-root": {
            backgroundColor: colors.primary[400],
            borderColor: `${colors.primary[700]} !important`,
            borderRadius: "0px",
            fontSize: "0.95rem",
            border: "0 !important",
          },
          "& .MuiDataGrid-cell": { borderColor: `${colors.primary[700]} !important` },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.primary[400],
            borderColor: `${colors.primary[700]} !important`,
          },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.primary[600],
          },
          // GridToolbar's buttons default to color="primary", which is the same navy as the grid
          // behind them. The three sibling CMMS grids carry this same rule.
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.gray[100]} !important`,
          },
          // A row opens the details drawer, so it should look like it does something.
          "& .MuiDataGrid-row": { cursor: "pointer" },
        }}
      >
        <DataGridPro
          apiRef={apiRef}
          loading={isLoading}
          slots={gridSlots}
          localeText={translations.components.MuiDataGrid.defaultProps.localeText}
          rows={nodes || []}
          getRowId={(row) => row.id}
          columns={columns}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
          onColumnOrderChange={handleColumnOrderChange}
          onColumnResize={handleColumnResize}
          onRowClick={(params) => onSelectNode?.(params.row)}
          // The Columns and Filters panels are portalled out of this Box, so their styling cannot
          // reach them from the sx above.
          slotProps={{ panel: { sx: getDataGridPanelSx(mode) } }}
          disableRowSelectionOnClick
        />
      </Box>
    </Paper>
  );
}
